<template>
  <div class="z-index-2">
    <div class="p-3 pb-0">
      <h6>{{ title }}</h6>
    </div>
    <div class="p-3">
      <div class="chart">
        <canvas :id="id" class="chart-canvas" :height="height"></canvas>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js/auto";
import ChartAnnotation from "chartjs-plugin-annotation";
import styles from "./UserSleepChart.vue?vue&type=style&index=0&lang=scss&module=1";
import Utils from "@/helpers/utils";

Chart.register(ChartAnnotation);

function indexToMin(index) {
  return index - 0.36;
}

function indexToMax(index) {
  return index + 0.36;
}

function getTotalHours(ctx, index) {
  const chart = ctx.chart;
  let total = 0;

  for (let i = 0; i < chart.data.datasets.length; i++) {
    let dataset = chart.data.datasets[i];
    if (dataset.type == "bar") {
      total += dataset.data[index];
    }
  }
  return total;
}

function average(ctx) {
  const values = ctx.chart.data.datasets[4].data;
  let average = values.reduce((a, b) => a + b, 0) / values.length;
  return average;
}

let annotations = [
  {
    id: "lineAvg",
    type: "line",
    borderColor: styles.colorLimitline,
    borderDash: [6, 6],
    borderDashOffset: 0,
    borderWidth: 1,
    label: {
      display: true,
      content: (ctx) => "Avg: " + average(ctx).toFixed(2),
      position: "start",
    },
    scaleID: "y",
    value: (ctx) => average(ctx),
  },
];
function addAnnotations(chart) {
  let maxDays = chart.data.datasets[0].data.length;

  for (let day = 0; day < maxDays; day++) {
    annotations.push({
      id: "totalHours" + day,
      type: "label",
      content: (ctx) => Utils.parseMinutesToHours(getTotalHours(ctx, day)),
      color: styles.light,
      position: {
        x: "center",
        y: "end",
      },
      font: {
        family: "NotoSans-Regular",
      },
      xMax: indexToMax(day) + 0.05,
      xMin: indexToMin(day) - 0.05,
      xScaleID: "x",
      yMax: (ctx) => getTotalHours(ctx, day),
      yMin: (ctx) => getTotalHours(ctx, day),
      yScaleID: "y1",
    });
  }
  chart.update();
}

export default {
  name: "UserSleepChart",
  props: {
    id: {
      type: String,
      default: "user-sleep-chart",
    },
    title: {
      type: String,
      default: "",
    },
    height: {
      type: String,
      default: "300",
    },
    chart: {
      type: Object,
      required: true,
      labels: Array,
      datasets: {
        type: Array,
        label: String,
        data: Array,
      },
    },
  },
  data() {
    return {};
  },
  watch: {
    chart() {
      this.addChart();
    },
  },
  mounted() {
    Chart.defaults.borderColor = styles.darkGray;
    this.addChart();
  },
  methods: {
    addChart() {
      let ctx = document.getElementById(this.id).getContext("2d");
      let gradientStroke1 = ctx.createLinearGradient(0, 300, 0, 50);
      gradientStroke1.addColorStop(1, "rgba(53,150,133,0.2)");
      gradientStroke1.addColorStop(0, "transparent");

      let chartStatus = Chart.getChart(this.id);
      if (chartStatus != undefined) {
        chartStatus.destroy();
      }

      let chart = new Chart(ctx, {
        data: {
          labels: this.chart.labels,
          datasets: [
            {
              type: "bar",
              label: this.chart.datasets[0].label,
              backgroundColor: this.chart.datasets[0].backgroundColor,
              data: this.chart.datasets[0].data,
              maxBarThickness: 30,
              order: 2,
              yAxisID: "y1",
            },
            {
              type: "bar",
              label: this.chart.datasets[1].label,
              backgroundColor: this.chart.datasets[1].backgroundColor,
              data: this.chart.datasets[1].data,
              maxBarThickness: 30,
              order: 2,
              yAxisID: "y1",
            },
            {
              type: "bar",
              label: this.chart.datasets[2].label,
              backgroundColor: this.chart.datasets[2].backgroundColor,
              data: this.chart.datasets[2].data,
              maxBarThickness: 30,
              order: 2,
              yAxisID: "y1",
            },
            {
              type: "bar",
              label: this.chart.datasets[3].label,
              backgroundColor: this.chart.datasets[3].backgroundColor,
              data: this.chart.datasets[3].data,
              maxBarThickness: 30,
              borderRadius: 4,
              order: 2,
              yAxisID: "y1",
            },
            {
              type: "line",
              label: this.chart.datasets[4].label,
              tension: 0.4,
              pointRadius: 3,
              pointBackgroundColor: styles.colorSleep,
              borderColor: styles.colorSleep,
              borderWidth: 3,
              backgroundColor: gradientStroke1,
              data: this.chart.datasets[4].data,
              fill: true,
              order: 1,
              yAxisID: "y",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              callbacks: {
                label: function (context) {
                  let label = context.dataset.label || "";
                  if (label && !label.startsWith("Sleep")) {
                    label += ": ";

                    if (context.parsed.y !== null) {
                      label += Utils.parseMinutesToHours(context.parsed.y);
                    }
                    return label;
                  } else return (label += ": " + context.parsed.y);
                },
              },
            },
            annotation: {
              annotations: annotations,
            },
            legend: {
              position: "bottom",
              labels: {
                color: styles.colorAxes,
                boxWidth: 12,
                boxHeight: 12,
                padding: 40,
              },
            },
          },
          interaction: {
            intersect: false,
            mode: "index",
          },

          scales: {
            y1: {
              stacked: true,
              display: false,
              position: "right",
              max: 24 * 60,
              ticks: {
                stepSize: 100,
                color: styles.gray,
              },
              title: {
                display: true,
                text: "Sleep Duration (Minutes)",
                color: styles.colorAxes,
                font: {
                  family: "NotoSans-Regular",
                },
              },
              // grid line settings
              grid: {
                drawOnChartArea: false, // only want the grid lines for one axis to show up
              },
            },
            x: {
              stacked: true,
              grid: {
                drawBorder: false,
                display: true,
                drawOnChartArea: true,
                drawTicks: true,
                borderDash: [5, 5],
              },
              ticks: {
                display: true,
                color: styles.gray,
                padding: 10,
                font: {
                  size: 12,
                  style: "normal",
                  lineHeight: 2,
                },
              },
            },
            y: {
              display: true,
              position: "left",
              min: 0,
              max: 100,
              ticks: {
                stepSize: 10,
                color: styles.gray,
              },
              title: {
                display: true,
                text: this.chart.datasets[4].label,
                color: styles.colorAxes,
                font: {
                  family: "NotoSans-Regular",
                },
              },
              // grid line settings
              grid: {
                drawOnChartArea: true,
              },
            },
          },
        },
      });

      addAnnotations(chart);
    },
  },
};
</script>
<style lang="scss" scoped>
:export {
  gray: $color-muted-text;
  darkGray: $color-gridlines;
  light: #fff;
  colorSleep: $color-sleep;
  colorAxes: $color-chart-axes-text;
  colorLimitline: $color-chart-limitline;
}
</style>
